export const GEN_STATUS_OPTIONS = [
    { value: "draft", label: "草稿" },
    { value: "preparing", label: "处理中" },
    { value: "generating", label: "生成中" },
    { value: "done", label: "已完成" },
    { value: "failed", label: "失败" },
];

export const PUBLISH_STATUS_OPTIONS = [
    { value: "unpublished", label: "未发布" },
    { value: "published", label: "已发布" },
];

const GEN_TAG = {
    draft: "info",
    preparing: "warning",
    generating: "warning",
    done: "success",
    failed: "danger",
};

const MEMBER_STATUS = {
    pending: "待生成",
    running: "生成中",
    done: "已完成",
    failed: "失败",
};

export const genStatusLabel = (value, fallback) => {
    if (fallback) return fallback;
    const found = GEN_STATUS_OPTIONS.find(item => item.value === value);
    return found ? found.label : (value || "—");
};

export const genStatusTag = (value) => GEN_TAG[value] || "info";

export const publishStatusLabel = (value, fallback) => {
    if (fallback) return fallback;
    const found = PUBLISH_STATUS_OPTIONS.find(item => item.value === value);
    return found ? found.label : (value || "未发布");
};

export const memberStatusLabel = (value, fallback) => {
    if (fallback) return fallback;
    return MEMBER_STATUS[value] || value || "—";
};

export const parseScope = (scope) => {
    if (!scope) return {};
    if (typeof scope === "object") return scope;
    try {
        return JSON.parse(scope);
    } catch (e) {
        return {};
    }
};

const namesByIds = (list, ids) => {
    if (!ids || !ids.length) return "";
    const map = {};
    (list || []).forEach(item => {
        map[String(item.id)] = item.name;
    });
    return ids.map(id => map[String(id)] || id).join("、");
};

/** 勾选值与选项 id 对齐，避免数字和字符串对不上导致回显为空 */
const alignIds = (selected, list) => {
    const raw = selected || [];
    if (!list || !list.length) return raw.slice();
    const byKey = {};
    list.forEach(item => {
        byKey[String(item.id)] = item.id;
    });
    return raw.map(id => (byKey[String(id)] !== undefined ? byKey[String(id)] : id));
};

export const formatScope = (scopeRaw, options, dataType) => {
    const scope = parseScope(scopeRaw);
    const opt = options || {};
    const stageNames = namesByIds(opt.stages, scope.stageIds);
    const groupNames = namesByIds(opt.userGroups, scope.userGroupIds);
    const areaText = !scope.areas || !scope.areas.length || scope.areas.indexOf("全部") >= 0
        ? "全部区域"
        : scope.areas.join("、");
    let source = "";
    if (dataType === "homework") {
        const homeworkNames = namesByIds(opt.homeworks, scope.homeworkIds);
        source = homeworkNames ? `作业：${homeworkNames}` : "作业：全部";
    } else if (dataType === "gsw" || dataType === "gsq") {
        if (scope.gsqMode === "date") {
            source = `观思问：${scope.dateStart || ""} 至 ${scope.dateEnd || ""}`;
        } else {
            const cycleNames = namesByIds(opt.cycles, scope.cycleIds);
            source = cycleNames ? `观思问：${cycleNames}` : "观思问：全部周期";
        }
    }
    return [stageNames || "全部阶段", areaText, groupNames, source].filter(Boolean).join(" · ");
};

export const emptyScopeForm = () => ({
    stageAll: true,
    stageIds: [],
    groupAll: true,
    userGroupIds: [],
    areaAll: true,
    areas: [],
    gsqMode: "cycle",
    cycleAll: true,
    cycleIds: [],
    dateStart: "",
    dateEnd: "",
    homeworkAll: true,
    homeworkIds: [],
});

const coversAll = (selected, list) => {
    if (!selected || !selected.length) return true;
    if (!list || !list.length) return false;
    return selected.length >= list.length;
};

export const scopeToForm = (scopeRaw, options) => {
    const scope = parseScope(scopeRaw);
    const opt = options || {};
    const stageIds = scope.stageIds || [];
    const userGroupIds = scope.userGroupIds || [];
    const cycleIds = scope.cycleIds || [];
    const homeworkIds = scope.homeworkIds || [];
    const areas = scope.areas || [];
    const areaAll = !areas.length || areas.indexOf("全部") >= 0;
    return {
        stageAll: coversAll(stageIds, opt.stages),
        stageIds: alignIds(stageIds, opt.stages),
        groupAll: coversAll(userGroupIds, opt.userGroups),
        userGroupIds: alignIds(userGroupIds, opt.userGroups),
        areaAll,
        areas: areaAll ? [] : areas.slice(),
        gsqMode: scope.gsqMode === "date" ? "date" : "cycle",
        cycleAll: scope.gsqMode !== "date" && coversAll(cycleIds, filteredCycles(opt.cycles, stageIds, coversAll(stageIds, opt.stages))),
        cycleIds: alignIds(cycleIds, opt.cycles),
        dateStart: scope.dateStart || "",
        dateEnd: scope.dateEnd || "",
        homeworkAll: !homeworkIds.length,
        homeworkIds: alignIds(homeworkIds, opt.homeworks),
    };
};

const filteredCycles = (cycles, stageIds, stageAll) => {
    if (stageAll) return cycles || [];
    const keys = {};
    (stageIds || []).forEach(id => {
        keys[String(id)] = true;
    });
    return (cycles || []).filter(item => keys[String(item.stageId)]);
};

const idsOrAll = (isAll, selected, list) => {
    if (isAll) return (list || []).map(item => item.id);
    return selected || [];
};

export const formToScope = (form, dataType, options) => {
    const opt = options || {};
    const isGsw = dataType === "gsw";
    return {
        stageIds: idsOrAll(form.stageAll, form.stageIds, opt.stages),
        userGroupIds: idsOrAll(form.groupAll, form.userGroupIds, opt.userGroups),
        areas: form.areaAll ? [] : (form.areas || []),
        gsqMode: isGsw ? form.gsqMode : "cycle",
        cycleIds: isGsw && form.gsqMode === "cycle"
            ? idsOrAll(form.cycleAll, form.cycleIds, filteredCycles(opt.cycles, form.stageIds, form.stageAll))
            : [],
        dateStart: isGsw && form.gsqMode === "date" ? (form.dateStart || null) : null,
        dateEnd: isGsw && form.gsqMode === "date" ? (form.dateEnd || null) : null,
        homeworkIds: dataType === "homework"
            ? (form.homeworkAll ? [] : (form.homeworkIds || []))
            : [],
    };
};
